import { DialogOpenEvent } from '@theme/dialog';
import './blake-mill-size-charts.js';

// Cache the source markup, not live nodes: every dialog needs independent tabs and IDs.
const chartRequests = new Map();

async function getCharts(url) {
  if (!chartRequests.has(url)) {
    const request = fetch(url, { signal: AbortSignal.timeout(10000) })
      .then(async (response) => {
        if (!response.ok) throw new Error('Size guide unavailable');
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const section = parsed.querySelector('.bm-size--charts');
        if (!section?.querySelector('bm-size-charts [data-size-panel]')) throw new Error('Size charts not found');
        section.querySelectorAll('script, link').forEach((element) => element.remove());
        return section.outerHTML;
      })
      .catch((error) => {
        chartRequests.delete(url);
        throw error;
      });
    chartRequests.set(url, request);
  }
  return chartRequests.get(url);
}

function prepareCharts(markup, prefix) {
  const parsed = new DOMParser().parseFromString(markup, 'text/html');
  const section = parsed.body.firstElementChild;
  const ids = new Map();
  section.querySelectorAll('[id]').forEach((element) => {
    const previous = element.id;
    element.id = `${prefix}-${previous}`;
    ids.set(previous, element.id);
  });
  section.querySelectorAll('*').forEach((element) => {
    for (const attribute of ['for', 'aria-controls', 'aria-labelledby', 'aria-describedby', 'headers']) {
      const value = element.getAttribute(attribute);
      if (value) element.setAttribute(attribute, value.split(/\s+/).map((id) => ids.get(id) || id).join(' '));
    }
    const href = element.getAttribute('href');
    if (href?.startsWith('#') && ids.has(href.slice(1))) element.setAttribute('href', `#${ids.get(href.slice(1))}`);
  });
  // Garment blocks emit per-chart rules to hide unused measurement columns.
  section.querySelectorAll('style').forEach((style) => {
    for (const [previous, next] of ids) {
      const escaped = previous.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      style.textContent = style.textContent.replace(new RegExp(`#${escaped}(?![\\w-])`, 'g'), `#${next}`);
    }
  });
  section.querySelector('bm-size-charts').dataset.editor = 'false';
  return document.importNode(section, true);
}

class SizeGuideContent extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.dialog = this.closest('dialog-component');
    this.dialog?.addEventListener(DialogOpenEvent.eventName, this.load, { signal: this.controller.signal });
    if (this.closest('dialog')?.open) this.load();
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  load = async () => {
    this.closest('.bm-size-guide__body').scrollTop = 0;
    if (this.loaded || this.loading) return;
    this.loading = true;
    const status = this.querySelector('.bm-size-guide__status');
    const fallback = this.querySelector('.bm-size-guide__fallback');
    status.textContent = 'Loading size charts…';
    status.hidden = false;
    fallback.hidden = true;
    this.setAttribute('aria-busy', 'true');
    try {
      const url = new URL(this.dataset.guideUrl, location.origin);
      url.searchParams.set('view', 'size-charts-cro');
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      const markup = await getCharts(url.href);
      if (!this.isConnected) return;
      this.querySelector('.bm-size-guide__charts').replaceChildren(prepareCharts(markup, this.dataset.idPrefix));
      this.loaded = true;
      status.hidden = true;
    } catch {
      if (!this.isConnected) return;
      status.textContent = 'The interactive charts could not load. Use the size information below or open the full size guide.';
      fallback.hidden = false;
    } finally {
      this.loading = false;
      this.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('bm-size-guide-content')) customElements.define('bm-size-guide-content', SizeGuideContent);
